import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { OverreadFixedComment, Permission, StudyCoughEventType } from "../../../misc/api/apiTypes";
import { Authorization } from "../../../misc/components/Authorization";
import * as Actions from "../actions";
import { AnalyseState, CoughOverreadFixedComment, EditModeEnum, PlayStateEnum } from "../interfaces/state";
import { PeaksJsTime, PeaksJsWaveform } from "../interfacesPeaks/PeaksJS";
import { AnalysisSetsButton } from "./AnalysisSetsButton";
import { DownloadButton } from "./DownloadButton";
import { EventListButton } from "./EventListButton";
import { FixedCommentsButton } from "./FixedCommentsButton";
import EventActionDashboard from "./EventActionDashboard";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { OnIgnoreEnter } from "../AnalyseApp";

export interface ActionDashboardProps {
    studyId: string;
    sessionId: string;
    selectedWaveform: string;
    availableEventTypes: StudyCoughEventType[];
    selectedEventType: StudyCoughEventType;
    selectedFixedCommentsCount: number;
    selectedAnalysisSetsCount: number;
    waveform: PeaksJsWaveform;
    isDownloading: boolean;
    dispatch: Dispatch<AnalyseState>;
    configErrorMessage: string;
    sessionNotesCount: number;
    acceptanceErrorMessage: string;
    selectedSegment: number;
    isSegmented: boolean;
}

export const ActionDashboard = ({ studyId, sessionId, selectedWaveform, selectedFixedCommentsCount, selectedAnalysisSetsCount, waveform, isDownloading, configErrorMessage, dispatch, sessionNotesCount, acceptanceErrorMessage, selectedSegment, isSegmented }: ActionDashboardProps) => {
    const { currentUserPermissions } = useSecurityContext();

    const getHourSegmentsListBtn = (isLoadedCorrectly: boolean) => {
        if (isLoadedCorrectly) {
            return (
                <button
                    id="hourSegmentsListBtn"
                    type="button"
                    className="btn btn-primary"
                    onClick={() => dispatch(Actions.OpenHourSegmentsModalAction.create())}
                    onKeyDown={OnIgnoreEnter}
                >
                    Hour Segments List
                </button>
            );
        } else {
            return null;
        }
    };

    if (waveform) {
        return (
            <div className="row">
                <EventActionDashboard />
                <div className="col-md-6 text-right">
                    {getHourSegmentsListBtn(configErrorMessage === null)}
                    &nbsp;
                    <EventListButton
                        acceptanceErrorMessageVisible={acceptanceErrorMessage !== null}
                        onClick={() =>
                            dispatch(Actions.OpenEventListDialogAction.create())
                        }
                    />&nbsp;
                    <Authorization
                        permissionId={PermissionEnum.ViewAllCoughSessionNotes}
                        currentPermissions={currentUserPermissions}
                        studyId={studyId}
                    >
                        <button
                            className="btn btn-primary"
                            onClick={() =>
                                dispatch(
                                    Actions.ToggleSessionNotesVisibilityAction.create()
                                )
                            }
                            onKeyDown={OnIgnoreEnter}
                        >
                            <i className="fa fa-comments" /> Session Notes
                            &nbsp;<span className="badge badge-info">{sessionNotesCount}</span>
                        </button>&nbsp;
                    </Authorization>
                    <AnalysisSetsButton
                        activeAnalysisSetsCount={selectedAnalysisSetsCount}
                        onClick={() =>
                            dispatch(Actions.OpenAnalysisSetDialogAction.create())
                        }
                    />&nbsp;
                    <FixedCommentsButton
                        selectedCommentsCount={selectedFixedCommentsCount}
                        onClick={() =>
                            dispatch(Actions.OpenFixedCommentsDialogAction.create())
                        }
                    />&nbsp;
                    <Authorization
                        permissionId={PermissionEnum.DownloadCoughRecordings}
                        currentPermissions={currentUserPermissions}
                        studyId={studyId}
                    >
                        <DownloadButton
                            sessionId={sessionId}
                            waveformType={selectedWaveform}
                            split={selectedSegment}
                            isSplit={isSegmented}
                        />
                    </Authorization>
                </div>
            </div>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState) {
    const waveform = state.core.peaksJs ? state.core.peaksJs.waveform : null;

    let selectedFixedComments: CoughOverreadFixedComment[] = [];
    if (state.analysisSet.coughAnalysisSets.length > 0) {
        // Selecting the fixed comments that are selected in the primary analysis set.
        const primaryAnalysisSetId = state.analysisSet.primaryAnalystSetId;
        const primaryAnalysisSet = state.analysisSet.coughAnalysisSets.find(set => {
            return set.Id === primaryAnalysisSetId;
        });

        selectedFixedComments = state.core.availableFixedComments.filter(com => {
            return primaryAnalysisSet.FixedCommentIds.includes(com.id);
        });
    }

    return {
        studyId: state.core.studyId,
        sessionId: state.core.sessionMetadata.sessionId,
        availableEventTypes: state.core.availableEventTypes,
        selectedEventType: state.core.selectedEventType,
        selectedWaveform: state.core.selectedWaveform,
        isSegmented: state.core.isSegmented,
        selectedFixedCommentsCount: selectedFixedComments.length,
        selectedAnalysisSetsCount:
            state.analysisSet.coughAnalysisSets.length > 0
                ? state.analysisSet.coughAnalysisSets.filter(x => x.IsSelected).length
                : null,
        waveform,
        isDownloading: state.core.isDownloadClicked,
        configErrorMessage: state.core.configErrorMessage,
        sessionNotesCount: state.core.sessionNotes.length,
        acceptanceErrorMessage: state.core.acceptanceErrorMessage,
        selectedSegment: state.core.selectedSegment
    } as ActionDashboardProps;
}

export default connect<ActionDashboardProps, any, void>(mapStateToProps)(ActionDashboard);
